import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Professional Bar Chart Component
 */
export const ProfessionalBarChart = ({ 
  data, 
  title, 
  height = 300, 
  showValues = false,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'],
  darkMode = false 
}) => {
  const chartData = {
    labels: data.map(item => item.label),
    datasets: [
      {
        label: title || 'Data',
        data: data.map(item => item.value),
        backgroundColor: data.map((_, index) => colors[index % colors.length] + '80'), // 50% opacity
        borderColor: data.map((_, index) => colors[index % colors.length]),
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: !!title,
        text: title,
        color: darkMode ? '#F3F4F6' : '#1F2937',
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: darkMode ? '#374151' : '#FFFFFF',
        titleColor: darkMode ? '#F3F4F6' : '#1F2937',
        bodyColor: darkMode ? '#F3F4F6' : '#1F2937',
        borderColor: darkMode ? '#6B7280' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: darkMode ? '#9CA3AF' : '#6B7280',
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: darkMode ? '#374151' : '#F3F4F6',
          drawBorder: false,
        },
        ticks: {
          color: darkMode ? '#9CA3AF' : '#6B7280',
          font: {
            size: 12,
          },
          callback: function(value) {
            return value.toLocaleString();
          }
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <div className="w-full" style={{ height: `${height}px` }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

/**
 * Professional Line Chart Component
 */
export const ProfessionalLineChart = ({ 
  data, 
  title, 
  height = 300,
  color = '#3B82F6',
  darkMode = false,
  showArea = true 
}) => {
  const chartData = {
    labels: data.map(item => item.label),
    datasets: [
      {
        label: title || 'Data',
        data: data.map(item => item.value),
        borderColor: color,
        backgroundColor: showArea ? color + '20' : 'transparent',
        borderWidth: 3,
        fill: showArea,
        tension: 0.4,
        pointBackgroundColor: color,
        pointBorderColor: '#FFFFFF',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: !!title,
        text: title,
        color: darkMode ? '#F3F4F6' : '#1F2937',
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: darkMode ? '#374151' : '#FFFFFF',
        titleColor: darkMode ? '#F3F4F6' : '#1F2937',
        bodyColor: darkMode ? '#F3F4F6' : '#1F2937',
        borderColor: darkMode ? '#6B7280' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: darkMode ? '#9CA3AF' : '#6B7280',
          font: {
            size: 12,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: darkMode ? '#374151' : '#F3F4F6',
          drawBorder: false,
        },
        ticks: {
          color: darkMode ? '#9CA3AF' : '#6B7280',
          font: {
            size: 12,
          },
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <div className="w-full" style={{ height: `${height}px` }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

/**
 * Professional Doughnut Chart Component
 */
export const ProfessionalDoughnutChart = ({ 
  data, 
  title, 
  height = 300,
  colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
  darkMode = false 
}) => {
  const chartData = {
    labels: data.map(item => item.label),
    datasets: [
      {
        data: data.map(item => item.value),
        backgroundColor: colors.slice(0, data.length),
        borderColor: darkMode ? '#1F2937' : '#FFFFFF',
        borderWidth: 3,
        hoverBorderWidth: 4,
        cutout: '60%',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: darkMode ? '#F3F4F6' : '#1F2937',
          font: {
            size: 12,
          },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      title: {
        display: !!title,
        text: title,
        color: darkMode ? '#F3F4F6' : '#1F2937',
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        backgroundColor: darkMode ? '#374151' : '#FFFFFF',
        titleColor: darkMode ? '#F3F4F6' : '#1F2937',
        bodyColor: darkMode ? '#F3F4F6' : '#1F2937',
        borderColor: darkMode ? '#6B7280' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        }
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <div className="w-full flex flex-col items-center" style={{ height: `${height}px` }}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

/**
 * Chart Container with Loading State
 */
export const ChartContainer = ({ 
  children, 
  title, 
  isLoading = false, 
  className = "",
  darkMode = false 
}) => {
  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 transition-colors duration-300 ${className}`}>
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {title}
          </h3>
        )}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm dark:shadow-gray-900/20 transition-colors duration-300 ${className}`}>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {title}
        </h3>
      )}
      {children}
    </div>
  );
};

// Export all chart components
const ProfessionalCharts = {
  ProfessionalBarChart,
  ProfessionalLineChart,
  ProfessionalDoughnutChart,
  ChartContainer,
};

export default ProfessionalCharts;
